import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Undo as UndoIcon,
  Redo as RedoIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletsIcon,
  Attachment as AttachmentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Channels } from '@bandido/shared/constants';
import { StyledToolbar } from './styled';
import { useEditor } from '@frontend/features/cases/hooks/useEditor';

interface ToolbarProps {
  onUndo: () => void;
  onRedo: () => void;
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onBullets: () => void;
  onAttachment: () => void;
  isLoading: boolean;
  disabled: boolean;
}

export const Toolbar = React.memo(
  ({ onUndo, onRedo, onBold, onItalic, onUnderline, onBullets, onAttachment, isLoading, disabled }: ToolbarProps) => {
    const { t } = useTranslation();
    const { channel } = useEditor();

    return (
      <StyledToolbar
        sx={{
          padding: (theme) => theme.spacing(1, 0),
        }}
        disableGutters
      >
        <ToggleButtonGroup
          sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
          size="small"
        >
          <ToggleButton onClick={onUndo} value={t('noteEditor.controls.undo')}>
            <UndoIcon />
          </ToggleButton>
          <ToggleButton onClick={onRedo} value={t('noteEditor.controls.redo')}>
            <RedoIcon />
          </ToggleButton>

          <ToggleButton onClick={onBold} value={t('noteEditor.controls.bold')}>
            <BoldIcon />
          </ToggleButton>
          <ToggleButton onClick={onItalic} value={t('noteEditor.controls.italic')}>
            <ItalicIcon />
          </ToggleButton>
          <ToggleButton onClick={onUnderline} value={t('noteEditor.controls.underline')}>
            <UnderlineIcon />
          </ToggleButton>
          <ToggleButton onClick={onBullets} value={t('noteEditor.controls.bulletList')}>
            <BulletsIcon />
          </ToggleButton>
          <ToggleButton onClick={onAttachment} value={t('noteEditor.controls.attachments')}>
            <AttachmentIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <LoadingButton
          sx={{ display: 'flex', gap: 1 }}
          variant="contained"
          size="large"
          color="primary"
          loading={isLoading}
          disabled={disabled}
          type="submit"
        >
          <span>{channel === Channels.EMAIL ? t('noteEditor.send') : t('noteEditor.save')}</span>
        </LoadingButton>
      </StyledToolbar>
    );
  },
);

Toolbar.displayName = 'Toolbar';
